/**
 * Reminders - cross-invoice overview (Phase 13 Cluster A)
 *
 * Top-level list of every payment reminder across all invoices. Hooks
 * (`useReminderList`) and card component (`ReminderCard`) are reused from
 * the per-invoice reminder UI; this route is the "global" entry point.
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { EmptyState, FilterPill, GlassPanel, LoadingSpinner, PageHeader, Pagination, SearchInput } from '../components/ui';
import { ReminderCard } from '../components/reminders/ReminderCard';
import { useReminderList } from '../hooks/useReminders';
import { useSession } from '../hooks/useTenantContext';

const reminderSearchSchema = z.object({
  page: z.coerce.number().int().positive().optional().default(1),
  level: z.coerce.number().int().min(1).max(3).optional(),
  q: z.string().optional(),
});

type ReminderSearch = z.infer<typeof reminderSearchSchema>;

export const Route = createFileRoute('/reminders')({
  component: RemindersPage,
  validateSearch: reminderSearchSchema,
});

const levelOptions: Array<{ value: ReminderSearch['level'] | undefined; label: string }> = [
  { value: undefined, label: 'Alle Stufen' },
  { value: 1, label: '1. Mahnung' },
  { value: 2, label: '2. Mahnung' },
  { value: 3, label: 'Letzte Mahnung' },
];

function RemindersPage() {
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();

  const { data, isLoading, error } = useReminderList({
    page: search.page,
    limit: 40,
    level: search.level,
  });
  // Codex-006: see DashboardPage — treat session-load as loading so the list
  // shows a spinner on hard reload instead of a premature empty state.
  const { isLoading: sessionLoading } = useSession();

  // Sprint 006 A3: Suche. Die 001-Reminder-API kennt kein q (reference-only,
  // FR-SCOPE-001) — Filter läuft client-seitig über die geladene Seite
  // (limit 40; Mahnungsbestand ist klein). Server-seitiges q wäre ein
  // 001-API-Follow-up.
  const q = (search.q ?? '').trim().toLowerCase();
  const visibleReminders = q
    ? (data?.reminders ?? []).filter((r) => {
        const haystack = [
          r.invoice?.invoiceNumber,
          r.invoice?.customer?.companyName,
          r.notes,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      })
    : data?.reminders ?? [];

  const handlePageChange = (page: number) => {
    navigate({
      search: (prev: ReminderSearch) => ({ ...prev, page }),
      replace: true,
    });
  };

  const handleLevelChange = (level: ReminderSearch['level'] | undefined) => {
    navigate({
      search: (prev: ReminderSearch) => ({ ...prev, level, page: 1 }),
      replace: true,
    });
  };

  return (
    <ProtectedRoute>
      <AppShell>
        <PageHeader
          title="Mahnungen"
          subtitle={`${data?.total ?? 0} Mahnung${(data?.total ?? 0) !== 1 ? 'en' : ''}`}
        />

        {/* 006b P3: Filter-Kanon (PATTERNS.md §1) — volle Filter gehören in
            die GlassPanel-Zeile, nicht in die Header-children. */}
        <GlassPanel className="mb-6">
          <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
            <div className="flex-1">
              <SearchInput
                value={search.q ?? ''}
                onChange={(next) =>
                  navigate({
                    search: (prev: ReminderSearch) => ({ ...prev, q: next || undefined }),
                    replace: true,
                  })
                }
                placeholder="Suchen (Rechnungs-Nr., Kunde)…"
                data-testid="reminders-search"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {levelOptions.map((opt) => (
                <FilterPill
                  key={opt.label}
                  active={(search.level ?? undefined) === opt.value}
                  onClick={() => handleLevelChange(opt.value)}
                >
                  {opt.label}
                </FilterPill>
              ))}
            </div>
          </div>
        </GlassPanel>

        {/* Content */}
        {isLoading || sessionLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-400 mb-4">Fehler beim Laden der Mahnungen</p>
          </div>
        ) : visibleReminders.length === 0 ? (
          <EmptyState
            title={search.level || q ? 'Keine Treffer' : 'Keine Mahnungen'}
            description={
              search.level || q
                ? 'Passen Sie Suche oder Filter an, um weitere Mahnungen zu sehen.'
                : 'Mahnungen werden aus der Rechnungsansicht erstellt, sobald eine Zahlung überfällig ist.'
            }
          />
        ) : (
          <>
            <GlassPanel
              variant="subtle"
              padding="sm"
              className="overflow-hidden"
              data-testid="reminders-list-surface"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {visibleReminders.map((reminder) => (
                  <ReminderCard key={reminder.id} reminder={reminder} showInvoiceInfo />
                ))}
              </div>
            </GlassPanel>

            {data && data.totalPages > 1 && (
              <div className="flex justify-center mt-6">
                <Pagination
                  page={search.page ?? 1}
                  totalPages={data.totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </AppShell>
    </ProtectedRoute>
  );
}
